import { useState, useEffect, useCallback, useRef } from 'react';
import backupData from '../data/articles.json';

export interface Article {
  id: string;
  title: string;
  subtitle: string;
  url: string;
  publishedAt: string;
  imageUrl?: string;
  originalImageUrl?: string;
  language: 'en' | 'es';
  content?: string;
}

export function cleanImageUrl(url?: string): string | undefined {
  if (!url) return undefined;

  let cleaned = url;
  if (url.includes('wp-content/uploads')) {
    const match = url.match(/wp-content\/uploads\/(.+)/i);
    if (match) {
      cleaned = `/wp-content/uploads/${match[1]}`;
    } else {
      cleaned = '/wp-content/uploads';
    }
  } else if (url.includes('miro.medium.com')) {
    const lastSlash = url.lastIndexOf('/');
    if (lastSlash !== -1) {
      const filename = url.substring(lastSlash + 1).split(/[?#]/)[0];
      cleaned = `/wp-content/uploads/${filename}`;
    }
  }

  return cleaned;
}

export function cleanImagePaths(html?: string): string | undefined {
  if (!html) return html;

  // 1. Convert absolute wordpress upload URLs to relative, keeping original in data-original-src
  let cleaned = html.replace(
    /(src|srcset)=["'](https?:\/\/(?:www\.)?christreignsmedia\.com\/wp-content\/uploads\/[^"']+)["']/gi,
    (match, attr, val) => {
      const relativePath = val.replace(/https?:\/\/(?:www\.)?christreignsmedia\.com/i, '');
      return `${attr}="${relativePath}" data-original-src="${val}"`;
    }
  );

  // 2. Also cover relative paths without slash src="wp-content/uploads/..." -> src="/wp-content/uploads/..."
  cleaned = cleaned.replace(
    /(src|srcset)=["']wp-content\/uploads\/([^"']+)["']/gi,
    (match, attr, val) => {
      return `${attr}="/wp-content/uploads/${val}" data-original-src="/wp-content/uploads/${val}"`;
    }
  );

  // 3. Convert Medium image CDN URLs to relative local paths, keeping original in data-original-src
  cleaned = cleaned.replace(
    /(src|srcset)=["'](https:\/\/miro\.medium\.com\/[^\s"'>]+)["']/gi,
    (match, attr, val) => {
      try {
        const lastSlash = val.lastIndexOf('/');
        if (lastSlash !== -1) {
          const filename = val.substring(lastSlash + 1).split(/[?#]/)[0];
          return `${attr}="/wp-content/uploads/${filename}" data-original-src="${val}"`;
        }
      } catch (e) { }
      return match;
    }
  );

  // 4. Remove srcset completely from images to prevent browser from loading resized variants
  cleaned = cleaned.replace(/\s+srcset=["']([^"']+)["']/gi, '');

  return cleaned;
}

// In production (Netlify), relative paths resolve correctly.
// In development (localhost), prefix with the live domain so images load.
export function resolveImagePath(url?: string): string | undefined {
  if (!url) return undefined;
  if (process.env.NODE_ENV !== 'production' && url.startsWith('/wp-content/')) {
    return `https://christreignslab.com${url}`;
  }
  return url;
}

export function resolveHtmlImagePaths(html?: string): string | undefined {
  if (!html) return html;
  if (process.env.NODE_ENV !== 'production') {
    return html.replace(
      /(src)=["']\/wp-content\/uploads/gi,
      '$1="https://christreignslab.com/wp-content/uploads'
    );
  }
  return html;
}

export function detectLanguage(title: string, content?: string): 'en' | 'es' {
  const combined = `${title || ''} ${content || ''}`.toLowerCase();

  if (/[áéíóúñ¿¡í]/.test(combined)) {
    return 'es';
  }

  const esWords = [' de ', ' y ', ' en ', ' la ', ' el ', ' con ', ' para ', ' por ', ' una ', ' un ', ' los ', ' las ', ' del '];
  const enWords = [' the ', ' and ', ' of ', ' to ', ' in ', ' for ', ' is ', ' that ', ' with ', ' you ', ' was ', ' are ', ' this '];

  let esCount = 0;
  let enCount = 0;

  for (const word of esWords) {
    const matches = combined.match(new RegExp(word, 'g'));
    if (matches) esCount += matches.length;
  }

  for (const word of enWords) {
    const matches = combined.match(new RegExp(word, 'g'));
    if (matches) enCount += matches.length;
  }

  return esCount > enCount ? 'es' : 'en';
}

interface UseMediumArticlesResult {
  articles: Article[];
  loading: boolean;
  error: string | null;
  count: number;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch scraped Medium articles from the NestJS + MongoDB backend.
 * Uses REACT_APP_API_URL or defaults to the Render production URL.
 */
export function useMediumArticles(lang?: 'en' | 'es'): UseMediumArticlesResult {
  const [articles, setArticles] = useState<Article[]>(() => {
    return (backupData.articles || []).map((art: any) => ({
      id: art.id,
      title: art.title || '',
      subtitle: art.subtitle || '',
      url: art.url || '',
      publishedAt: art.publishedAt || '',
      imageUrl: cleanImageUrl(art.imageUrl),
      language: detectLanguage(art.title, art.subtitle),
    }));
  });
  const [loading, setLoading] = useState<boolean>(articles.length === 0);
  const [error, setError] = useState<string | null>(null);

  const isMounted = useRef(true);

  const fetchArticles = useCallback(async () => {
    if (!isMounted.current) return;
    setLoading(true);
    setError(null);

    try {
      const apiBase =
        process.env.REACT_APP_API_URL ||
        (process.env.NODE_ENV === 'production'
          ? 'https://christreignsmedia-api-b91e81f2bbea.herokuapp.com'
          : 'http://localhost:3001');

      const response = await fetch(`${apiBase}/articles`);
      if (!response.ok) {
        throw new Error(`Failed to fetch articles: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'API returned unsuccessful status');
      }

      const fetchedList: Article[] = (data.articles || []).map((art: any) => {
        let subtitle = art.subtitle || '';
        if (!subtitle && art.content) {
          const cleanText = art.content
            .replace(/<[^>]*>/g, '')
            .replace(/&[a-z0-9#]+;/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          subtitle = cleanText.length > 150 ? cleanText.slice(0, 150) + '...' : cleanText;
        }

        let imageUrl = art.imageUrl || undefined;
        if (!imageUrl && art.content) {
          const imgMatch = art.content.match(/<img[^>]+src=["']([^"']+)["']/i);
          if (imgMatch) {
            imageUrl = imgMatch[1];
          }
        }
        imageUrl = cleanImageUrl(imageUrl);

        const content = cleanImagePaths(art.content || undefined);

        return {
          id: art._id || art.id,
          title: art.title || '',
          subtitle,
          url: art.url || '',
          publishedAt: art.publishedAt || '',
          imageUrl,
          language: detectLanguage(art.title, content || subtitle),
          content,
        };
      });

      // Sort descending by publication date/time if available
      fetchedList.sort((a, b) => {
        const timeA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const timeB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        return timeB - timeA;
      });

      if (isMounted.current) {
        setArticles(fetchedList);
      }
    } catch (err: any) {
      if (isMounted.current) {
        console.error('[useMediumArticles] Error fetching backend articles:', err);
        setError(err.message || 'Failed to fetch articles.');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    fetchArticles();

    // Trigger a status wake-up in the background in case the server is asleep
    const apiBase =
      process.env.REACT_APP_API_URL ||
      (process.env.NODE_ENV === 'production'
        ? 'https://christreignsmedia-api-b91e81f2bbea.herokuapp.com'
        : 'http://localhost:3001');
    fetch(`${apiBase}/articles/status`).catch(() => { });

    return () => {
      isMounted.current = false;
    };
  }, [fetchArticles]);

  const filteredArticles = lang
    ? articles.filter((a) => a.language === lang)
    : articles;

  return {
    articles: filteredArticles,
    loading,
    error,
    count: filteredArticles.length,
    refetch: fetchArticles,
  };
}
